import { z } from 'zod';
import { SystemEntity, type ValidationRules } from '@erp/shared-types';
import type {
  ConvertTaskToTypeInput,
  TaskResponse,
  TaskTypeResponse,
} from '@erp/rpc';

/** Sentinel for "leave the task without a parent" in the parent picker. */
export const NO_PARENT_VALUE = '__none__';

/**
 * Workflow-created tasks record the business document they were raised for
 * in `metadata.sourceEntity` even when the step had no task type. That is the
 * anchor the task should have had; the form pre-fills from it.
 */
const SourceEntitySchema = z.object({
  entityType: z.enum(SystemEntity),
  entityId: z.uuid(),
});

export type TaskSourceEntity = z.infer<typeof SourceEntitySchema>;

export function readSourceEntity(
  metadata: TaskResponse['metadata'],
): TaskSourceEntity | null {
  const parsed = SourceEntitySchema.safeParse(metadata?.sourceEntity);
  return parsed.success ? parsed.data : null;
}

/** Why the anchor field shows a note beneath it, if any. */
export type AnchorHint =
  /** The id was pre-filled from the task's source entity. */
  | 'detected'
  /** The task has a source entity of a different type than the chosen type anchors to. */
  | 'mismatch'
  | null;

export type TaskTypeInputFieldWire = {
  fieldName: string;
  fieldType: string;
  label: string;
  isRequired: boolean;
  isMultiple: boolean;
  enumValues?: string[] | null;
  validationRules?: ValidationRules | null;
};

export interface SetTaskTypeFormState {
  declaredAnchorType: SystemEntity | null;
  /** Anchor id derived from the source entity when its type matches; '' otherwise. */
  detectedAnchorId: string;
  /** Effective anchor id: the user's edit, else the detected id. */
  anchorEntityId: string;
  anchorIdValid: boolean;
  /** True when the type needs no anchor, or the supplied id is a valid UUID. */
  anchorSatisfied: boolean;
  anchorHint: AnchorHint;
  inputFields: TaskTypeInputFieldWire[];
  /** True when required input fields have coercible values. */
  inputSatisfied: boolean;
  canSubmit: boolean;
}

/**
 * Pure derivation of the Set Type form from its inputs. `anchorIdInput` is
 * null until the user edits the field, so the detected id keeps applying.
 */
export function deriveSetTaskTypeForm({
  selectedType,
  sourceEntity,
  anchorIdInput,
  inputValues = {},
}: {
  selectedType: TaskTypeResponse | undefined;
  sourceEntity: TaskSourceEntity | null;
  anchorIdInput: string | null;
  inputValues?: Readonly<Record<string, string>>;
}): SetTaskTypeFormState {
  const declaredAnchorType = selectedType?.anchorEntityType ?? null;
  const sourceMatches =
    declaredAnchorType !== null &&
    sourceEntity !== null &&
    sourceEntity.entityType === declaredAnchorType;
  const detectedAnchorId = sourceMatches ? sourceEntity.entityId : '';
  const anchorEntityId = anchorIdInput ?? detectedAnchorId;
  const anchorIdValid = z.uuid().safeParse(anchorEntityId).success;
  const anchorSatisfied = declaredAnchorType === null || anchorIdValid;
  const inputFields = selectedType?.inputSchema?.fields ?? [];
  const inputSatisfied = isTaskTypeInputSatisfied(inputFields, inputValues);

  return {
    declaredAnchorType,
    detectedAnchorId,
    anchorEntityId,
    anchorIdValid,
    anchorSatisfied,
    anchorHint: resolveAnchorHint({
      declaredAnchorType,
      sourceEntity,
      detectedAnchorId,
      anchorEntityId,
    }),
    inputFields,
    inputSatisfied,
    canSubmit: selectedType !== undefined && anchorSatisfied && inputSatisfied,
  };
}

export function isTaskTypeInputSatisfied(
  fields: readonly TaskTypeInputFieldWire[],
  values: Readonly<Record<string, string>>,
): boolean {
  return fields.every((field) => {
    const raw = values[field.fieldName] ?? '';
    if (raw.trim() === '') return !field.isRequired;
    return coerceTaskInputFieldValue(field, raw).ok;
  });
}

export function parseTaskTypeInputBag(
  fields: readonly TaskTypeInputFieldWire[],
  values: Readonly<Record<string, string>>,
): Record<string, unknown> | undefined {
  if (fields.length === 0) return undefined;
  const bag: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[field.fieldName] ?? '';
    if (raw.trim() === '') continue;
    const coerced = coerceTaskInputFieldValue(field, raw);
    if (!coerced.ok) return undefined;
    bag[field.fieldName] = coerced.value;
  }
  return Object.keys(bag).length > 0 ? bag : undefined;
}

export function coerceTaskInputFieldValue(
  field: Pick<
    TaskTypeInputFieldWire,
    'fieldType' | 'isMultiple' | 'enumValues' | 'validationRules'
  >,
  raw: string,
): { ok: true; value: unknown } | { ok: false } {
  const typed = coerceTaskInputFieldType(field, raw);
  if (!typed.ok || violatesFieldConstraints(field, typed.value)) {
    return typed.ok ? { ok: false } : typed;
  }
  return typed;
}

type CoercedInput = { ok: true; value: unknown } | { ok: false };

function coerceInteger(trimmed: string): CoercedInput {
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? { ok: true, value: parsed } : { ok: false };
}

function coerceFiniteNumber(trimmed: string): CoercedInput {
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? { ok: true, value: parsed } : { ok: false };
}

function coerceBoolean(trimmed: string): CoercedInput {
  if (trimmed === 'true') return { ok: true, value: true };
  if (trimmed === 'false') return { ok: true, value: false };
  return { ok: false };
}

function coerceJsonObject(trimmed: string): CoercedInput {
  return parseJsonValue(
    trimmed,
    (value) =>
      value !== null && typeof value === 'object' && !Array.isArray(value),
  );
}

const TASK_INPUT_TYPE_COERCERS: Record<
  string,
  (trimmed: string) => CoercedInput
> = {
  integer: coerceInteger,
  float: coerceFiniteNumber,
  numeric: coerceFiniteNumber,
  currency: coerceFiniteNumber,
  boolean: coerceBoolean,
  json: coerceJsonObject,
  object: coerceJsonObject,
  range: coerceJsonObject,
  unit: coerceJsonObject,
  geospatial: coerceJsonObject,
};

function coerceTaskInputFieldType(
  field: Pick<TaskTypeInputFieldWire, 'fieldType' | 'isMultiple'>,
  raw: string,
): CoercedInput {
  const trimmed = raw.trim();
  if (field.isMultiple || field.fieldType === 'array') {
    return parseJsonValue(trimmed, Array.isArray);
  }
  const coerce = TASK_INPUT_TYPE_COERCERS[field.fieldType];
  return coerce ? coerce(trimmed) : { ok: true, value: trimmed };
}

function violatesFieldConstraints(
  field: Pick<
    TaskTypeInputFieldWire,
    'fieldType' | 'isMultiple' | 'enumValues' | 'validationRules'
  >,
  value: unknown,
): boolean {
  if (
    Array.isArray(value) &&
    (field.isMultiple || field.fieldType === 'array')
  ) {
    return value.some((item) => violatesScalarConstraints(field, item));
  }
  return violatesScalarConstraints(field, value);
}

function violatesScalarConstraints(
  field: Pick<TaskTypeInputFieldWire, 'enumValues' | 'validationRules'>,
  value: unknown,
): boolean {
  const allowed = field.enumValues ?? [];
  if (allowed.length > 0) {
    if (typeof value !== 'string' || !allowed.includes(value)) return true;
  }
  const rules = field.validationRules;
  if (!rules) return false;
  if (typeof value === 'string') return violatesStringRules(rules, value);
  if (typeof value === 'number') return violatesNumberRules(rules, value);
  return false;
}

function violatesStringRules(rules: ValidationRules, value: string): boolean {
  if (rules.minLength !== undefined && value.length < rules.minLength) {
    return true;
  }
  if (rules.maxLength !== undefined && value.length > rules.maxLength) {
    return true;
  }
  if (!rules.pattern) return false;
  try {
    return !new RegExp(rules.pattern).test(value);
  } catch {
    return true;
  }
}

function violatesNumberRules(rules: ValidationRules, value: number): boolean {
  if (rules.min !== undefined && value < rules.min) return true;
  if (rules.max !== undefined && value > rules.max) return true;
  return false;
}

function parseJsonValue(
  raw: string,
  predicate: (value: unknown) => boolean,
): { ok: true; value: unknown } | { ok: false } {
  try {
    const parsed: unknown = JSON.parse(raw);
    return predicate(parsed) ? { ok: true, value: parsed } : { ok: false };
  } catch {
    return { ok: false };
  }
}

function resolveAnchorHint({
  declaredAnchorType,
  sourceEntity,
  detectedAnchorId,
  anchorEntityId,
}: {
  declaredAnchorType: SystemEntity | null;
  sourceEntity: TaskSourceEntity | null;
  detectedAnchorId: string;
  anchorEntityId: string;
}): AnchorHint {
  if (!declaredAnchorType) return null;
  if (detectedAnchorId && anchorEntityId === detectedAnchorId) {
    return 'detected';
  }
  if (sourceEntity && !detectedAnchorId) return 'mismatch';
  return null;
}

/** The `tasks.convertToType` payload for the current form values. */
export function buildConvertTaskInput({
  taskId,
  taskTypeId,
  form,
  parentTaskId,
  inputValues = {},
}: {
  taskId: string;
  taskTypeId: string;
  form: Pick<
    SetTaskTypeFormState,
    'declaredAnchorType' | 'anchorEntityId' | 'inputFields'
  >;
  parentTaskId: string;
  inputValues?: Readonly<Record<string, string>>;
}): ConvertTaskToTypeInput {
  const input = parseTaskTypeInputBag(form.inputFields, inputValues);
  return {
    id: taskId,
    taskTypeId,
    ...(form.declaredAnchorType ? { anchorEntityId: form.anchorEntityId } : {}),
    ...(parentTaskId !== NO_PARENT_VALUE ? { parentTaskId } : {}),
    ...(input ? { input } : {}),
  };
}

/** Label for a parent candidate in the picker: "TASK-12 · Title" when numbered. */
export function formatParentCandidateLabel(
  task: Pick<TaskResponse, 'displayId' | 'title'>,
): string {
  return task.displayId ? `${task.displayId} · ${task.title}` : task.title;
}
